/* =====================================================================================
   Doing something with a result set.

   A ranked list of a hundred centres that cannot leave the screen is a demonstration. The work it
   exists for is exporting it, turning it into accounts somebody is accountable for, and coming
   back in a month to see what moved.

   THREE OPERATIONS, all tenant agnostic. Nothing here knows what a row means.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';
import type { Criterion } from './search';

const live = () => BACKEND === 'supabase';

/* ------------------------------------------------------------------ export */

/* Excel decides a field is a formula if it begins with =, +, - or @, so a name like "-Clinic" or
   a cell someone has pasted becomes executable on open. Prefixing a quote is the standard
   defence and is invisible in the sheet. This costs nothing and the alternative is shipping a
   file that runs whatever a cell says. */
const csvCell = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};

export function toCsv(columns: { key: string; label: string }[],
                      rows: Record<string, unknown>[]): string {
  const head = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvCell(r[c.key])).join(',')).join('\n');
  // A BOM, so Excel opens UTF-8 correctly rather than mangling every accented hospital name.
  return `﻿${head}\n${body}\n`;
}

export function downloadCsv(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick rather than immediately: revoking synchronously cancels the
  // download in Safari, which is the sort of thing that only shows up on somebody else's laptop.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ------------------------------------------------------------------ saved searches */

export interface SavedSearch {
  id: string; module: string; name: string; description?: string | null;
  criteria: Criterion[]; result_count?: number | null; updated_at?: string;
}

export async function listSavedSearches(module: string): Promise<SavedSearch[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').from('saved_searches')
    .select('id, module, name, description, criteria, result_count, updated_at')
    .eq('module', module).order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as SavedSearch[];
}

export async function saveSearch(
  module: string, name: string, criteria: Criterion[], resultCount: number,
): Promise<void> {
  if (!live()) throw new Error('Saving a search needs the database, and this build has none.');
  const { supabase } = await import('./supabase');
  /* Upsert on the name, because the second thing anyone does after saving is adjust and save
     again, and a name that already exists should be replaced rather than refused. org_id is left
     to its column default so the row lands in the caller's org and RLS agrees with it. */
  const { error } = await supabase().schema('app').from('saved_searches')
    .upsert({ module, name: name.trim(), criteria, result_count: resultCount },
            { onConflict: 'org_id,module,name' });
  if (error) throw new Error(error.message);
}

export async function deleteSavedSearch(id: string): Promise<void> {
  if (!live()) return;
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('saved_searches').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/* ------------------------------------------------------------------ into the CRM */

export interface AccountSeed {
  siteId: string; name: string; country: string; city?: string;
  region?: string; kinds?: string[]; tier?: number;
}

/* Account kinds are a longer list than site kinds and the two only partly overlap, so anything
   unrecognised becomes 'other' rather than failing the whole batch on one check constraint. */
const ACCOUNT_KINDS = new Set(['transplant_centre', 'hi_lab', 'reference_lab', 'opo']);

export async function createAccountsFromSites(seeds: AccountSeed[]): Promise<{
  created: number; existing: number;
}> {
  if (!live()) throw new Error('Creating accounts needs the database, and this build has none.');
  if (!seeds.length) return { created: 0, existing: 0 };
  const { supabase } = await import('./supabase');

  const rows = seeds.map((s) => ({
    name: s.name,
    country: s.country,
    city: s.city ?? null,
    region_state: s.region ?? null,
    kinds: (s.kinds ?? []).filter((k) => ACCOUNT_KINDS.has(k)).length
      ? (s.kinds ?? []).filter((k) => ACCOUNT_KINDS.has(k))
      : ['other'],
    tier: s.tier === 1 ? 'tier_1' : s.tier === 2 ? 'tier_2' : 'tier_3',
    status: 'prospect',
    atlas_site_id: dbSiteId(s.siteId),
    notes: 'Created from an Atlas search.',
  }));

  /* ignoreDuplicates, against the partial unique index added in migration 0049. Running this
     twice must not produce two accounts for one hospital: that splits its calls, opportunities
     and invoices across two records and no total is right afterwards. Postgres decides what is
     already there, not the client, so two people clicking at once is also safe. */
  const { data, error } = await supabase().schema('crm').from('accounts')
    .upsert(rows, { onConflict: 'org_id,atlas_site_id', ignoreDuplicates: true })
    .select('id');
  if (error) throw new Error(error.message);

  const created = (data ?? []).length;
  return { created, existing: rows.length - created };
}
